// Generic style settings shared by every block, written as Bootstrap 5 utility classes.
import { classList, replaceClasses } from './dom.js';
import { BREAKPOINTS } from './grid.js';

// ---- Spacing (m-*, p-*, base breakpoint) -------------------------------------------------------

export const SPACING_SIZES = ['0', '1', '2', '3', '4', '5'];
const SPACING_CLASS = /^([mp])([tbsexy]?)-(0|1|2|3|4|5|auto)$/;
const SIDES = ['t', 'b', 's', 'e'];

export function isSpacingClass(name) {
    return SPACING_CLASS.test(name);
}

/** Reads {t, b, s, e} for margins ('m') or paddings ('p'); '' when unset. Specific classes win. */
export function readSpacing(el, property) {
    const values = { t: '', b: '', s: '', e: '' };
    const rank = { t: -1, b: -1, s: -1, e: -1 };
    const weight = { '': 0, x: 1, y: 1, t: 2, b: 2, s: 2, e: 2 };
    const covers = { '': SIDES, x: ['s', 'e'], y: ['t', 'b'], t: ['t'], b: ['b'], s: ['s'], e: ['e'] };
    for (const name of classList(el)) {
        const match = name.match(SPACING_CLASS);
        if (!match || match[1] !== property) continue;
        for (const side of covers[match[2]]) {
            if (weight[match[2]] >= rank[side]) {
                rank[side] = weight[match[2]];
                values[side] = match[3];
            }
        }
    }
    return values;
}

/** Writes {t, b, s, e} back with the shortest set of classes (m-3, my-2 mx-auto, mt-1...). */
export function writeSpacing(el, property, values) {
    const v = { t: values.t || '', b: values.b || '', s: values.s || '', e: values.e || '' };
    const classes = [];
    if (v.t && v.t === v.b && v.t === v.s && v.t === v.e) {
        classes.push(`${property}-${v.t}`);
    } else {
        if (v.t && v.t === v.b) classes.push(`${property}y-${v.t}`);
        else {
            if (v.t) classes.push(`${property}t-${v.t}`);
            if (v.b) classes.push(`${property}b-${v.b}`);
        }
        if (v.s && v.s === v.e) classes.push(`${property}x-${v.s}`);
        else {
            if (v.s) classes.push(`${property}s-${v.s}`);
            if (v.e) classes.push(`${property}e-${v.e}`);
        }
    }
    const pattern = new RegExp(`^${property}[tbsexy]?-(?:0|1|2|3|4|5|auto)$`);
    replaceClasses(el, pattern, classes);
}

// ---- Visibility per breakpoint (d-none, d-md-block...) -----------------------------------------

const DISPLAY_CLASS = /^d(?:-(sm|md|lg|xl|xxl))?-(none|inline|inline-block|block|grid|inline-grid|table|table-row|table-cell|flex|inline-flex)$/;

export function isDisplayClass(name) {
    return DISPLAY_CLASS.test(name);
}

/** The display value used when an element is shown again at a breakpoint. */
export function naturalDisplay(el) {
    if (el.classList.contains('row') || el.classList.contains('d-flex')) return 'flex';
    for (const name of classList(el)) {
        const match = name.match(DISPLAY_CLASS);
        if (match && match[2] !== 'none') return match[2];
    }
    const inline = ['a', 'span', 'img', 'button', 'strong', 'em'];
    return inline.includes(el.localName) ? 'inline-block' : 'block';
}

/** {'' : false, sm: false, md: true, ...}: true when the element is hidden at that breakpoint. */
export function readHidden(el) {
    const own = {};
    for (const name of classList(el)) {
        const match = name.match(DISPLAY_CLASS);
        if (match) own[match[1] || ''] = match[2] === 'none';
    }
    const hidden = {};
    let current = false;
    for (const bp of BREAKPOINTS) {
        if (own[bp.key] !== undefined) current = own[bp.key];
        hidden[bp.key] = current;
    }
    return hidden;
}

export function writeHidden(el, hidden) {
    const display = naturalDisplay(el);
    const classes = [];
    let current = false;
    const baseDisplay = classList(el).find(name => /^d-(?!none)[a-z-]+$/.test(name) && DISPLAY_CLASS.test(name) && !/^d-(?:sm|md|lg|xl|xxl)-/.test(name));
    for (const bp of BREAKPOINTS) {
        const value = !!hidden[bp.key];
        if (value !== current) {
            classes.push(bp.key ? `d-${bp.key}-${value ? 'none' : display}` : (value ? 'd-none' : `d-${display}`));
            current = value;
        }
    }
    // Keep a display class the user chose on purpose (d-flex on a box) when nothing is hidden.
    if (!classes.length && baseDisplay) classes.push(baseDisplay);
    replaceClasses(el, DISPLAY_CLASS, classes);
}

// ---- Colors and decorations ---------------------------------------------------------------------

export const THEME_COLORS = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];

export const BACKGROUND_CLASS = /^bg-(primary|secondary|success|danger|warning|info|light|dark|white|transparent|body|body-secondary|body-tertiary)(?:-subtle)?$/;
export const TEXT_COLOR_CLASS = /^text-(primary|secondary|success|danger|warning|info|light|dark|white|body|muted|black-50|white-50|body-secondary|body-emphasis)(?:-emphasis)?$/;
export const TEXT_ALIGN_CLASS = /^text-(start|center|end)$/;
export const ROUNDED_CLASS = /^rounded(?:-(0|1|2|3|4|5|circle|pill))?$/;
/** Every corner of Bootstrap: plain "rounded", then 1 to 5, pill and circle. */
export const ROUNDED_OPTIONS = [
    { value: '', label: 'option.square' },
    { value: 'rounded', label: 'option.rounded' },
    ...['1', '2', '3', '4', '5'].map(n => ({ value: `rounded-${n}`, label: 'option.rounded', suffix: ` ${n}` })),
    { value: 'rounded-pill', label: 'option.pill' },
    { value: 'rounded-circle', label: 'option.circle' },
];
export const SHADOW_CLASS = /^shadow(?:-(sm|lg|none))?$/;
export const BORDER_CLASS = /^border(?:-(0|1|2|3|4|5|top|bottom|start|end|primary|secondary|light|dark))?$/;

/** Reads one inline style property ('' when unset). */
export function readStyle(el, property) {
    return el.style.getPropertyValue(property) || '';
}

export function writeStyle(el, property, value) {
    if (value === '' || value === null || value === undefined) el.style.removeProperty(property);
    else el.style.setProperty(property, value);
    if (!el.getAttribute('style')) el.removeAttribute('style');
}

/** Background image with cover sizing, as inline styles (the URL is checked by the caller). */
export function writeBackgroundImage(el, url) {
    if (!url) {
        ['background-image', 'background-size', 'background-position', 'background-repeat'].forEach(p => writeStyle(el, p, ''));
        return;
    }
    writeStyle(el, 'background-image', `url("${String(url).replace(/["\\\n\r]/g, encodeURIComponent)}")`);
    writeStyle(el, 'background-size', 'cover');
    writeStyle(el, 'background-position', 'center');
    writeStyle(el, 'background-repeat', 'no-repeat');
}

export function readBackgroundImage(el) {
    const value = readStyle(el, 'background-image');
    const match = value.match(/url\((['"]?)(.*?)\1\)/);
    return match ? decodeURIComponent(match[2]) : '';
}

/** rgb()/rgba() from getComputedStyle to #rrggbb, for the color inputs. */
export function toHex(color) {
    const match = String(color || '').match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return /^#[0-9a-f]{6}$/i.test(color) ? color : '';
    return '#' + match.slice(1, 4).map(n => parseInt(n, 10).toString(16).padStart(2, '0')).join('');
}
